// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Link,
    Tooltip,
    makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { IAppConfigResult, useAppConfig } from '../../../libs/hooks/useAppConfig';
import { IAppConfigValue } from '../../../libs/services/AppConfigService';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { Alert } from '../../../redux/features/app/AppState';
import { customTokens, useDialogClasses } from '../../../styles';

interface IEffectivePromptsDialogProps {
    alert: Alert;
}

const useClasses = makeStyles({
    link: {
        fontSize: customTokens.fontSizeBase300,
        textDecorationLine: 'underline',
    },
    dialogContent: {
        '> h3': {
            fontWeight: '600',
            marginTop: '0',
        },
    },
});

export const EffectivePromptsDialog: React.FC<IEffectivePromptsDialogProps> = ({ alert }) => {
    const [promptGuidanceContent, setPromptGuidanceContent] = useState<string>('');
    const classes = useClasses();
    const dialogClasses = useDialogClasses();

    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const user = emailAddress;

    const appConfig = useAppConfig();

    const getPromptGuidanceContent = async () => {
        const response: IAppConfigResult<IAppConfigValue> = await appConfig.getConfigSingleValue(
            'Prompt Guidance',
            user,
        );

        if (response.Success) {
            setPromptGuidanceContent(response.Data?.Value ?? '');
        }
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getPromptGuidanceContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!promptGuidanceContent) {
        return null; // Add fallback UI
    }

    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={'Show prompt guidance'} relationship="label">
                    <Link className={classes.link}>{alert.message}</Link>
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                            </DialogTrigger>
                        }
                    >
                        Prompt guidance for eya
                    </DialogTitle>
                    <DialogContent>
                        <div
                            className={classes.dialogContent}
                            dangerouslySetInnerHTML={{ __html: promptGuidanceContent }}
                        />
                    </DialogContent>
                    <DialogActions position="start" className={dialogClasses.footer}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">Close</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
